/**
 * useTier — fetches the current user's exam-app entitlement.
 *
 * As of v3 (per-cert subscriptions), the response includes `ownedCerts` —
 * the list of cert codes the user has an active $14.99/mo subscription
 * for. Components should call `hasAccessToCert(tier, certCode, email,
 * { ownedCerts, legacyAllAccess })` to decide whether to show the
 * Subscribe pill or unlock the card.
 *
 * Resolution order in the backend mirrored here:
 *   - Owner email => tier='pro', source='owner'
 *   - Free open beta (skills-platform switch) => tier='pro', source='beta',
 *     betaMode=true. Every cert is free; callers should also hide payment UI.
 *   - Per-cert exam sub => tier='exam' (mapped to 'student' for UI), ownedCerts populated
 *   - Legacy all-access exam sub => tier='exam' (mapped to 'pro' for UI), legacyAllAccess=true
 *   - Platform sub => tier='pro' or 'student', source='platform'
 *   - None => tier='free'
 *
 * SAFETY: if the network call fails we fall back to 'free' with empty
 * ownedCerts. The backend is the source of truth — this hook only drives
 * UI affordances.
 */
import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { OWNER_EMAILS } from './certTiers';
import config from './config';

export function useTier() {
  const [tier, setTier] = useState('free');
  const [source, setSource] = useState('none'); // 'exam' | 'platform' | 'owner' | 'none'
  const [email, setEmail] = useState('');
  const [ownedCerts, setOwnedCerts] = useState([]);
  const [legacyAllAccess, setLegacyAllAccess] = useState(false);
  // Free open beta. Starts false so a slow/failed load never shows free copy
  // on a paid platform.
  const [betaMode, setBetaMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const session = await fetchAuthSession().catch(() => null);
        const claims = session && session.tokens && session.tokens.idToken && session.tokens.idToken.payload;
        const userEmail = (claims && claims.email) || '';
        const jwt = (session && session.tokens && session.tokens.idToken && session.tokens.idToken.toString()) || '';
        if (!cancelled) setEmail(userEmail);

        if (OWNER_EMAILS.includes(userEmail)) {
          if (!cancelled) {
            setTier('pro');
            setSource('owner');
            setLegacyAllAccess(true);
            setIsLoading(false);
          }
          return;
        }
        if (!jwt) {
          if (!cancelled) setIsLoading(false);
          return;
        }
        const r = await fetch(`${config.apiUrl}/billing/my-subscription`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!r.ok) {
          if (!cancelled) { setTier('free'); setIsLoading(false); }
          return;
        }
        const data = await r.json().catch(() => ({}));
        const raw = (data && data.tier) || 'free';
        const lac = Boolean(data && data.legacyAllAccess);
        const beta = Boolean(data && data.betaMode);
        const certs = Array.isArray(data && data.ownedCerts) ? data.ownedCerts : [];
        // UI-tier mapping:
        //   - exam + legacyAllAccess => 'pro' (unlocks everything)
        //   - exam per-cert => 'student' (gate is per-cert via ownedCerts)
        //   - team => 'pro'
        //   - else passthrough
        let ui = raw;
        if (raw === 'exam') ui = lac ? 'pro' : 'student';
        else if (raw === 'team') ui = 'pro';
        if (!cancelled) {
          setTier(ui);
          setSource(data?.source || 'none');
          setOwnedCerts(certs);
          setLegacyAllAccess(lac);
          setBetaMode(beta);
          setStripeCustomerId(data?.stripeCustomerId || null);
          setCancelAtPeriodEnd(Boolean(data?.cancelAtPeriodEnd));
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err && err.message);
          setTier('free');
          setSource('none');
          setOwnedCerts([]);
          setLegacyAllAccess(false);
          setBetaMode(false);
          setIsLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return {
    tier,
    source,
    email,
    ownedCerts,
    legacyAllAccess,
    betaMode,
    isLoading,
    error,
    stripeCustomerId,
    cancelAtPeriodEnd,
  };
}
